import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiHelpCircle, FiX, FiSend, FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';
import supportService, { TIPOS_TICKET } from '../../services/supportService';
import { useAuthStore, useSettingsStore, useToastStore } from '../../store';
import { construirContexto } from '../../utils/supportContext';
import './SupportButton.css';

const SOPORTE_URL = 'https://soporte.consoltechsv.com/';

/**
 * Botón de ayuda flotante: abre un ticket de soporte sin salir del sistema.
 *
 * El usuario solo escribe QUÉ le pasó. Todo lo demás —desde qué taller, quién
 * es, en qué pantalla estaba, con qué navegador— lo adjunta el sistema solo.
 * Pedirle esos datos a un mecánico en medio del taller es como no pedirlos:
 * o no los sabe, o los escribe mal, o no reporta nada.
 *
 * El contexto se muestra ANTES de enviar, desplegable. Nada se manda a
 * espaldas de quien reporta.
 */
const SupportButton = () => {
  const { user, isAuthenticated } = useAuthStore();
  const { settings } = useSettingsStore();
  const toast = useToastStore();
  const location = useLocation();

  const [abierto, setAbierto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [verContexto, setVerContexto] = useState(false);
  const [enviado, setEnviado] = useState(null);
  const [form, setForm] = useState({ tipo: 'error', titulo: '', descripcion: '' });

  // El contexto se congela al ABRIR el panel, no al enviar: si el usuario
  // navega a otra pantalla con el panel abierto, lo que se reporta sigue
  // siendo la pantalla donde encontró el problema.
  const [contexto, setContexto] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && abierto && !enviando) cerrar();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, enviando]);

  if (!isAuthenticated) return null;

  const abrir = () => {
    setContexto(construirContexto({ user, settings, pathname: location.pathname }));
    setForm({ tipo: 'error', titulo: '', descripcion: '' });
    setEnviado(null);
    setVerContexto(false);
    setAbierto(true);
  };

  const cerrar = () => {
    setAbierto(false);
    setEnviado(null);
  };

  const enviar = async (e) => {
    e.preventDefault();
    if (!form.titulo.trim()) {
      toast.error('Escriba un título breve');
      return;
    }
    if (!form.descripcion.trim()) {
      toast.error('Describa qué ocurrió');
      return;
    }

    try {
      setEnviando(true);
      const resultado = await supportService.crearTicket({
        tipo: form.tipo,
        titulo: form.titulo.trim(),
        descripcion: form.descripcion.trim(),
        contexto,
      });
      setEnviado(resultado);
      toast.success('Ticket enviado');
    } catch (err) {
      toast.error(err.message || 'No se pudo enviar el ticket');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="fab-supportbutton"
        onClick={abrir}
        title="Ayuda y soporte"
        aria-label="Ayuda y soporte"
      >
        <FiHelpCircle />
      </button>

      {abierto && (
        <div
          className="overlay-supportbutton"
          onClick={(e) => {
            if (e.target === e.currentTarget && !enviando) cerrar();
          }}
        >
          <div className="panel-supportbutton" role="dialog" aria-modal="true" aria-label="Soporte">
            <div className="header-supportbutton">
              <h3>{enviado ? 'Ticket enviado' : '¿Necesita ayuda?'}</h3>
              <button
                type="button"
                className="close-supportbutton"
                onClick={cerrar}
                disabled={enviando}
                aria-label="Cerrar"
              >
                <FiX />
              </button>
            </div>

            {enviado ? (
              <div className="exito-supportbutton">
                <p>
                  Su reporte quedó registrado
                  {enviado.numero ? (
                    <>
                      {' '}con el número <strong>{enviado.numero}</strong>
                    </>
                  ) : null}
                  . El equipo de ConsolTech lo revisará y le responderá.
                </p>
                <a
                  href={enviado.url || SOPORTE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-supportbutton"
                >
                  {enviado.url ? 'Seguir mi ticket' : 'Ir a soporte'} <FiExternalLink />
                </a>
                {enviado.url && (
                  <p className="nota-supportbutton">
                    Guarde ese enlace: es la forma de seguir su reporte y responder.
                  </p>
                )}
                <button type="button" className="btn-primary-supportbutton" onClick={cerrar}>
                  Cerrar
                </button>
              </div>
            ) : (
              <form onSubmit={enviar} className="form-supportbutton">
                <div className="field-supportbutton">
                  <label htmlFor="soporte-tipo">¿Qué necesita?</label>
                  <select
                    id="soporte-tipo"
                    value={form.tipo}
                    onChange={(e) => setForm((p) => ({ ...p, tipo: e.target.value }))}
                    disabled={enviando}
                  >
                    {TIPOS_TICKET.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field-supportbutton">
                  <label htmlFor="soporte-titulo">Resumen</label>
                  <input
                    id="soporte-titulo"
                    type="text"
                    maxLength={140}
                    placeholder="En una línea: qué pasó"
                    value={form.titulo}
                    onChange={(e) => setForm((p) => ({ ...p, titulo: e.target.value }))}
                    disabled={enviando}
                    autoFocus
                  />
                </div>

                <div className="field-supportbutton">
                  <label htmlFor="soporte-descripcion">Cuéntenos con detalle</label>
                  <textarea
                    id="soporte-descripcion"
                    rows={5}
                    maxLength={2000}
                    placeholder={
                      'Qué estaba haciendo, qué esperaba que pasara y qué pasó en su lugar.\n' +
                      'Si el problema se repite, cuéntenos los pasos para provocarlo.'
                    }
                    value={form.descripcion}
                    onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))}
                    disabled={enviando}
                  />
                  <span className="contador-supportbutton">{form.descripcion.length}/2000</span>
                </div>

                {/* El usuario ve exactamente qué se adjunta. Nada va a sus espaldas. */}
                <button
                  type="button"
                  className="toggle-contexto-supportbutton"
                  onClick={() => setVerContexto((v) => !v)}
                >
                  {verContexto ? <FiChevronUp /> : <FiChevronDown />}
                  Se adjunta automáticamente su taller, usuario y pantalla
                </button>

                {verContexto && contexto && (
                  <dl className="contexto-supportbutton">
                    <div>
                      <dt>Taller</dt>
                      <dd>{contexto.taller.nombre || '—'} ({contexto.taller.codigo || '—'})</dd>
                    </div>
                    <div>
                      <dt>Usuario</dt>
                      <dd>{contexto.usuario.nombre} · {contexto.usuario.correo}</dd>
                    </div>
                    <div>
                      <dt>Pantalla</dt>
                      <dd>{contexto.pantalla} <code>{contexto.ruta}</code></dd>
                    </div>
                    <div>
                      <dt>Equipo</dt>
                      <dd>
                        {contexto.navegador} en {contexto.sistemaOperativo} · {contexto.tipoEquipo}{' '}
                        ({contexto.pantallaTamano})
                      </dd>
                    </div>
                    <p className="nota-supportbutton">
                      No se envían datos de sus clientes ni su contraseña.
                    </p>
                  </dl>
                )}

                <div className="acciones-supportbutton">
                  <button
                    type="button"
                    className="btn-cancel-supportbutton"
                    onClick={cerrar}
                    disabled={enviando}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary-supportbutton" disabled={enviando}>
                    <FiSend /> {enviando ? 'Enviando...' : 'Enviar ticket'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SupportButton;
